import { toast } from '@wellesley/system-toast'
import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import {
  Post,
  Language,
  usePostApiV1PostsByPostIdTranslateMutation,
} from '@wellesley/openapi'
import { useTranslationConfigs } from '@wellesley/settings'
import { feedsSlice } from '../../../../feedsSlice'

import getIsTranslatableText from '../../helpers/getIsTranslatableText'

const useTranslation = (post: Post) => {
  const { preferredLanguage, checkIfTranslatable } = useTranslationConfigs()

  const langKey = preferredLanguage?.toLowerCase() || ''
  const cachedTranslation = post.translations?.[langKey] || ''
  const translation = preferredLanguage ? cachedTranslation : ''

  const [isLoading, setIsLoading] = useState(false)
  const [isOriginalText, setIsOriginalText] = useState(true)

  useEffect(() => {
    if (!post.translations) setIsOriginalText(true)
  }, [post.translations])

  const isTranslatableText = getIsTranslatableText(post.text)
  const canTranslate = checkIfTranslatable(post.lang) && isTranslatableText

  const [translateRequest] = usePostApiV1PostsByPostIdTranslateMutation()

  const showOriginalText = () => setIsOriginalText(true)

  const dispatch = useDispatch()

  const saveTranslation = (text: string) => {
    if (!preferredLanguage) return

    dispatch(
      feedsSlice.actions.updatePost({
        ...post,
        translations: {
          ...(post.translations || {}),
          [preferredLanguage]: text,
        },
      }),
    )
  }

  const makeTranslate = async () => {
    if (translation) setIsOriginalText(false)
    else {
      setIsLoading(true)
      try {
        const translatedText = await translateRequest({
          postId: post.id,
          lang: preferredLanguage as Language,
          htmlContent: true,
        }).unwrap()

        if (!translatedText) return

        setIsOriginalText(false)
        saveTranslation(translatedText.text)
      } catch (error) {
        toast.error(`Failed to translate post`)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return {
    isLoading,
    canTranslate,
    translation,
    isOriginalText,
    makeTranslate,
    showOriginalText,
  }
}

export default useTranslation
